import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  MapPin,
  Phone,
  Mail,
  Cpu,
  Shield,
  Cloud,
} from "lucide-react";

const Footer = () => {
  const linkHover = { scale: 1.05, color: "#4bdfec" };
  const iconHover = { scale: 1.2, rotate: 5 };

  return (
    <footer style={styles.footer}>
      {/* BACKGROUND ANIMATED TECH ICONS */}
      <div style={styles.bgIcons}>
        {[Cpu, Shield, Cloud].map((Icon, i) => (
          <motion.div
            key={i}
            style={styles.bgIcon}
            animate={{ y: [0, -25, 0], opacity: [0.08, 0.15, 0.08] }}
            transition={{ duration: 6 + i * 2, repeat: Infinity }}
          >
            <Icon size={90} />
          </motion.div>
        ))}
      </div>

      <div style={styles.container}>
        {/* ABOUT */}
        <div style={styles.section}>
          <img src="/images/logo.png" alt="Zyntro Logo" style={styles.logo} />

          <p style={styles.text}>
            <strong>Zyntro Software Solutions Pvt. Ltd.</strong> is a
            technology-driven enterprise delivering secure, scalable, and
            intelligent IT solutions across India. We specialize in Mobile
            Device Management (MDM), cloud infrastructure, cybersecurity,
            enterprise software development, and digital transformation.
          </p>
        </div>

        {/* SERVICES */}
        <div style={styles.section}>
          <h3 style={styles.title}>Services</h3>
          {serviceLinks.map(([label, path], i) => (
            <motion.div key={i} whileHover={linkHover}>
              <Link to={path} style={styles.link}>
                {label}
              </Link>
            </motion.div>
          ))}
        </div>

        {/* COMPANY */}
        <div style={styles.section}>
          <h3 style={styles.title}>Company</h3>
          {companyLinks.map(([label, path], i) => (
            <motion.div key={i} whileHover={linkHover}>
              <Link to={path} style={styles.link}>
                {label}
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CONTACT + SOCIAL */}
        <div style={styles.section}>
          <h3 style={styles.title}>Contact Info</h3>

          <motion.div style={styles.contactItem} whileHover={linkHover}>
            <MapPin size={18} /> Bhadohi, Uttar Pradesh, India
          </motion.div>

          <motion.a
            href="tel:+918787044997"
            style={styles.contactItem}
            whileHover={linkHover}
          >
            <Phone size={18} /> +91 87870 44997
          </motion.a>

          <motion.a
            href="mailto:info@zyntro.in"
            style={styles.contactItem}
            whileHover={linkHover}
          >
            <Mail size={18} /> info@zyntro.in
          </motion.a>

          <motion.a
            href="mailto:support@zyntro.in"
            style={styles.contactItem}
            whileHover={linkHover}
          >
            <Mail size={18} /> support@zyntro.in
          </motion.a>

          {/* SOCIAL ICONS */}
          <div style={styles.social}>
            <motion.a
              href="https://www.facebook.com/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={iconHover}
              style={styles.socialIcon}
            >
              <Facebook size={22} />
            </motion.a>

            <motion.a
              href="https://twitter.com/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={iconHover}
              style={styles.socialIcon}
            >
              <Twitter size={22} />
            </motion.a>

            <motion.a
              href="https://www.instagram.com/zyntro_software"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={iconHover}
              style={styles.socialIcon}
            >
              <Instagram size={22} />
            </motion.a>

            <motion.a
              href="https://www.linkedin.com/company/zyntro-software-solution/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={iconHover}
              style={styles.socialIcon}
            >
              <Linkedin size={22} />
            </motion.a>
          </div>
        </div>
      </div>

      <div style={styles.bottomBar}>
        © 2025 Zyntro Software Solution Pvt. Ltd. | All Rights Reserved
      </div>
    </footer>
  );
};

/* ---------------- DATA ---------------- */

const serviceLinks = [
  ["Android Zero Touch", "/services/android-zero-touch-enrollment"],
  ["Android Management", "/services/android-management"],
  ["Cloud Computing", "/services/cloud-computing"],
  ["Cloud Managed Services", "/services/cloud-management-service"],
  ["Cyber Security", "/services/cyber-security"],
  ["IT Management", "/services/it-management"],
  ["IT Support", "/services/it-support"],
];

const companyLinks = [
  ["Home", "/"],
  ["About Us", "/company"],
  ["Leadership Message", "/leadership-message"],
  ["Portfolio", "/portfolio"],
  ["Blog", "/blog"],
  ["Contact", "/contact"],
];

/* ---------------- STYLES ---------------- */

const styles = {
  footer: {
    position: "relative",
    background: "linear-gradient(135deg, #050329, #0b0b1a, #132f35)",
    color: "#fff",
    padding: "70px 20px 20px",
    overflow: "hidden",
  },
  bgIcons: {
    position: "absolute",
    inset: 0,
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    pointerEvents: "none",
  },
  bgIcon: {
    color: "rgba(75,223,236,0.08)",
  },
  container: {
    position: "relative",
    maxWidth: "1200px",
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "40px",
    zIndex: 2,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  logo: {
    width: "140px",
    marginBottom: "12px",
  },
  title: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "8px",
  },
  text: {
    fontSize: "15px",
    lineHeight: "1.8",
    color: "#cfd8dc",
    textAlign: "justify",
  },
  link: {
    color: "#cfd8dc",
    textDecoration: "none",
    fontSize: "15px",
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "15px",
    color: "#cfd8dc",
    textDecoration: "none",
  },
  social: {
    display: "flex",
    gap: "14px",
    marginTop: "12px",
  },
  socialIcon: {
    color: "#ffffff",
  },
  bottomBar: {
    marginTop: "40px",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    paddingTop: "15px",
    textAlign: "center",
    fontSize: "14px",
    color: "#9ea7ad",
  },
};

export default Footer;